import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

interface Column {
  key: string;
  label: string;
  sortable?: boolean;
  render?: (value: any, row: any) => React.ReactNode;
}

interface Action {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: (row: any) => void;
  className?: string;
  hidden?: boolean;
}

export interface FilterConfig {
  key: string;
  label: string;
  type: 'text' | 'select' | 'multiselect' | 'date' | 'daterange' | 'boolean';
  options?: { value: any; label: string }[];
  placeholder?: string;
  nestedKey?: string; // Para campos anidados, ej: 'sucursal.cliente.nombre'
  dependsOn?: string; // Deshabilita el filtro hasta que el padre tenga valor
}

export interface ServerPagination {
  page: number;
  pageSize: number;
  hasMore: boolean;
  totalCount?: number;
  onPageChange: (page: number) => void;
}

interface DataTableProps {
  data: any[];
  columns: Column[];
  actions?: Action[];
  title?: string;
  onAdd?: () => void;
  onEdit?: (row: any) => void;
  onDelete?: (row: any) => void;
  onView?: (row: any) => void;
  searchPlaceholder?: string;
  itemsPerPage?: number;
  customActions?: (row: any) => React.ReactNode;
  viewMode?: 'grid' | 'table';
  loading?: boolean;
  emptyMessage?: string;
  onRefresh?: () => void;
  filters?: FilterConfig[];
  enableAdvancedFilters?: boolean;
  onFilterChange?: (filters: Record<string, any>) => void;
  initialFilters?: Record<string, any>;
  serverPagination?: ServerPagination;
  defaultShowFilters?: boolean;
  searchHint?: string;
  /** Total de registros antes de filtrar. Si no se pasa, se usa `data.length`. */
  totalCount?: number;
  /** Persiste búsqueda/filtros/página en la URL (listados client-side). */
  persistInUrl?: boolean;
}

/** Convierte un valor de celda a texto buscable (evita .toLowerCase en números/objetos). */
function valueToSearchString(value: any): string {
  if (value == null || value === '') return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map(valueToSearchString).filter(Boolean).join(' ');
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    const parts = [
      o.nombre,
      o.name,
      o.label,
      o.alias,
      o.mail,
      o.codigo,
      o.description,
      o.descripcion,
    ].filter((p) => p != null && typeof p !== 'object');
    return parts.map(String).join(' ');
  }
  return String(value);
}

export function ServerPageBar({
  page,
  pageSize,
  hasMore,
  rowCount,
  totalCount,
  loading,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  hasMore: boolean;
  rowCount: number;
  totalCount?: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
}) {
  const from = rowCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to =
    totalCount != null
      ? Math.min(page * pageSize, totalCount)
      : (page - 1) * pageSize + rowCount;
  const totalPages =
    totalCount != null && totalCount > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : null;
  const maxVisiblePages = 10;
  const startPage =
    totalPages == null
      ? page
      : Math.max(1, Math.min(page - Math.floor(maxVisiblePages / 2), totalPages - maxVisiblePages + 1));
  const endPage = totalPages == null ? page : Math.min(totalPages, startPage + maxVisiblePages - 1);
  const pageNumbers =
    totalPages == null
      ? [page]
      : Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  return (
    <div className="bg-transparent px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:px-4">
      <p className="text-xs text-gray-700 dark:text-gray-300">
        Mostrando <span className="font-medium">{from}</span> a{' '}
        <span className="font-medium">{to}</span>
        {totalCount != null ? (
          <>
            {' '}
            de <span className="font-medium">{totalCount}</span>
          </>
        ) : hasMore ? (
          ' · hay más resultados'
        ) : (
          ''
        )}
      </p>
      <div className="inline-flex rounded-md shadow-sm -space-x-px">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1 || loading}
          className="relative inline-flex items-center px-3 py-1.5 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Anterior
        </button>
        {pageNumbers.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onPageChange(n)}
            disabled={loading}
            className={`relative inline-flex items-center px-3 py-1.5 border text-xs font-medium ${
              n === page
                ? 'z-10 bg-primary-50 dark:bg-primary-900/20 border-primary-500 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {n}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={
            loading || (totalPages != null ? page >= totalPages : !hasMore)
          }
          className="relative inline-flex items-center px-3 py-1.5 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Siguiente
        </button>
      </div>
    </div>
  );
}

const DataTable: React.FC<DataTableProps> = ({
  data,
  columns,
  actions,
  title,
  onAdd,
  onEdit: _onEdit,
  onDelete: _onDelete,
  onView: _onView,
  searchPlaceholder = 'Buscar...',
  itemsPerPage = 10,
  customActions,
  viewMode = 'table',
  loading = false,
  emptyMessage = 'No hay datos disponibles',
  onRefresh,
  filters = [],
  enableAdvancedFilters = true,
  onFilterChange,
  initialFilters = {},
  serverPagination,
  defaultShowFilters = false,
  searchHint,
  totalCount,
  persistInUrl = false,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlBootstrapped = useRef(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(defaultShowFilters);
  const [advancedFilters, setAdvancedFilters] = useState<Record<string, any>>(initialFilters);
  const searchTimerRef = useRef<number | undefined>(undefined);

  // Restaurar estado desde URL (solo client-side, una vez)
  useEffect(() => {
    if (!persistInUrl || serverPagination || urlBootstrapped.current) return;
    urlBootstrapped.current = true;
    const q = searchParams.get('q') || '';
    const pageRaw = Number(searchParams.get('page') || '1');
    const restored: Record<string, any> = {};
    filters.forEach((f) => {
      if (f.type === 'daterange') {
        const desde = searchParams.get(`${f.key}_desde`);
        const hasta = searchParams.get(`${f.key}_hasta`);
        if (desde) restored[`${f.key}_desde`] = desde;
        if (hasta) restored[`${f.key}_hasta`] = hasta;
        return;
      }
      const v = searchParams.get(f.key);
      if (v == null || v === '') return;
      if (f.type === 'boolean') restored[f.key] = v === 'true';
      else if (/^\d+$/.test(v) && f.type === 'select') restored[f.key] = Number(v);
      else restored[f.key] = v;
    });
    if (q) setSearchTerm(q);
    if (Object.keys(restored).length > 0) {
      setAdvancedFilters((prev) => ({ ...prev, ...restored }));
      setShowFilters(true);
    }
    if (Number.isFinite(pageRaw) && pageRaw > 1) setCurrentPage(pageRaw);
  }, [persistInUrl, serverPagination, searchParams, filters]);

  // Escribir URL al cambiar filtros/búsqueda/página (client-side)
  useEffect(() => {
    if (!persistInUrl || serverPagination || !urlBootstrapped.current) return;
    const next = new URLSearchParams();
    if (searchTerm.trim()) next.set('q', searchTerm.trim());
    Object.entries(advancedFilters).forEach(([key, value]) => {
      if (value === '' || value == null || value === undefined) return;
      if (Array.isArray(value) && value.length === 0) return;
      next.set(key, String(value));
    });
    if (currentPage > 1) next.set('page', String(currentPage));
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    persistInUrl,
    serverPagination,
    searchTerm,
    advancedFilters,
    currentPage,
    searchParams,
    setSearchParams,
  ]);

  const emitServerFilters = (nextFilters: Record<string, any>, rawSearch: string) => {
    if (!onFilterChange) return;
    const payload = { ...nextFilters };
    const term = rawSearch.trim();
    if (term.length >= 3) {
      payload.q = term;
    } else {
      delete payload.q;
    }
    onFilterChange(payload);
  };

  // Actualizar filtros cuando cambien las opciones pero mantener valores existentes
  // Solo actualizar si los initialFilters realmente cambiaron (no en cada render)
  React.useEffect(() => {
    const rest = { ...initialFilters };
    delete rest.q;
    if (Object.keys(rest).length > 0) {
      setAdvancedFilters((prev) => {
        const hasChanges = Object.keys(rest).some((key) => prev[key] !== rest[key]);
        if (hasChanges) {
          return {
            ...prev,
            ...rest,
          };
        }
        return prev;
      });
    }
  }, [initialFilters]);

  // Función para buscar en propiedades anidadas
  const searchInNestedProperties = (obj: any, searchTerm: string): boolean => {
    const term = searchTerm.toLowerCase();

    // Campos comunes para todas las entidades
    const commonFields = [
      obj.id?.toString(),
      obj.nombre,
      obj.apellido,
      obj.numero?.toString(),
      obj.telefono,
      obj.email,
      obj.mail,
      obj.alias,
      obj.descripcion,
      // Campos anidados comunes
      obj.cliente?.nombre,
      obj.role?.name,
      obj.role?.nombre,
      obj.vendedor?.alias,
      obj.vendedor?.nombre,
      obj.tipoDeCliente?.nombre,
      obj.tipoDeServicio?.nombre,
    ];

    // Campos específicos para relevamientos
    const relevamientoFields = [
      obj.fecha?.toString(),
      obj.fechaFin?.toString(),
      obj.fechaRecepcion?.toString(),
      obj.sucursal?.nombre,
      obj.sucursal?.cliente?.nombre,
      obj.usuario?.nombre,
      obj.usuario?.apellido,
      obj.estadoTarea?.nombre,
      obj.tipoDeProducto?.nombre,
      obj.tipoDeElemento?.nombre,
      obj.tarea?.nombre,
    ];

    // Campos específicos para remitos (número formateado)
    const remitoFields = [
      obj.letra,
      obj.secuencia,
      obj.numero?.toString(),
      obj.estadoRemito?.nombre,
      // Número formateado: letra + secuencia + numero con padding
      obj.letra && obj.secuencia && obj.numero
        ? `${obj.letra}${obj.secuencia}${obj.numero.toString().padStart(4, '0')}`
        : null,
      // También buscar sin padding
      obj.letra && obj.secuencia && obj.numero ? `${obj.letra}${obj.secuencia}${obj.numero}` : null,
    ];

    // Campos específicos para tareas
    const tareaFields = [
      obj.tipoDeTarea?.nombre,
      obj.estadoTarea?.nombre,
      obj.prioridad?.nombre,
      obj.usuario?.alias,
      obj.usuario?.nombre,
      obj.sucursal?.nombre,
      obj.sucursal?.cliente?.nombre,
      obj.tipoDeProducto?.nombre,
    ];

    // Campos específicos para solicitudes (enfocado en estado)
    const solicitudFields = [
      obj.estadoTarea?.nombre, // Prioridad al estado para solicitudes
      obj.tipoSolicitud?.nombre,
      obj.prioridad?.nombre,
      obj.usuario?.alias,
      obj.usuario?.nombre,
      obj.sucursal?.nombre,
      obj.sucursal?.cliente?.nombre,
      obj.nombre,
    ];

    // Campos específicos para órdenes de trabajo (número formateado)
    const ordenTrabajoFields = [
      obj.numero?.toString(),
      // Número formateado: OT-{numero} con padding
      obj.numero ? `OT-${obj.numero.toString().padStart(6, '0')}` : null,
      // También buscar sin padding
      obj.numero ? `OT-${obj.numero}` : null,
      // Buscar solo el número sin el prefijo
      obj.numero?.toString(),
    ];

    // Campos específicos para extintores (prioridad a sucursal)
    const extintorFields = [
      obj.sucursal?.nombre, // Prioridad a sucursal para extintores
      obj.cliente?.nombre,
      obj.interno?.toString(),
      obj.nroSerie,
      obj.tipoDeCarga?.nombre,
      obj.iram,
      obj.codigo,
      obj.ubicacion,
    ];

    // Campos específicos para puestos (prioridad a nombre)
    const puestoFields = [
      obj.nombre, // Prioridad a nombre para puestos
      obj.sucursal?.nombre,
      obj.sucursal?.cliente?.nombre,
      obj.ubicacion,
      obj.codigo,
      obj.extintor?.codigo,
      obj.extintor?.nroSerie,
    ];

    // Campos específicos para elementos/instalaciones fijas (prioridad a tipo de elemento)
    const elementoFields = [
      obj.tipoDeElemento?.nombre, // Prioridad a tipo de elemento
      obj.sucursal?.nombre,
      obj.sucursal?.cliente?.nombre,
      obj.ubicacion,
      obj.interno?.toString(),
      obj.id?.toString(),
    ];

    // Campos específicos para centers (prioridad a name)
    const centerFields = [
      obj.name, // Prioridad a name para centers
      obj.description,
    ];

    // Campos específicos para QRs (prioridad a sucursal)
    const qrFields = [
      obj.sucursal, // Prioridad a sucursal para QRs
      obj.cliente,
      obj.tipoProducto,
      obj.tipoElemento,
      obj.producto,
      obj.codigo,
    ];

    // Combinar todos los campos
    const searchFields = [
      ...commonFields,
      ...relevamientoFields,
      ...remitoFields,
      ...tareaFields,
      ...solicitudFields,
      ...ordenTrabajoFields,
      ...extintorFields,
      ...puestoFields,
      ...elementoFields,
      ...centerFields,
      ...qrFields,
    ];

    const found = searchFields.some((field) => {
      const s = valueToSearchString(field);
      return s.length > 0 && s.toLowerCase().includes(term);
    });

    // Debug: mostrar qué campos se están buscando
    if (searchTerm && searchTerm.length > 0) {
      console.log(
        'Buscando:',
        term,
        'en campos:',
        searchFields.filter((f) => f),
        'Resultado:',
        found,
      );
    }

    return found;
  };

  // Función helper para obtener valores de campos anidados
  const getNestedValue = (obj: any, path: string): any => {
    if (!path) return obj;
    return path.split('.').reduce((current, prop) => current?.[prop], obj);
  };

  // Generar opciones para filtros de tipo select/multiselect
  const generateFilterOptions = (filter: FilterConfig) => {
    const uniqueValues = Array.from(
      new Set(
        data
          .map((item) => {
            const value = filter.nestedKey
              ? getNestedValue(item, filter.nestedKey)
              : item[filter.key];
            return value;
          })
          .filter(Boolean),
      ),
    );

    return uniqueValues.map((value) => {
      // Si el valor es un objeto con propiedades comunes, usar nombre o label
      if (typeof value === 'object' && value !== null) {
        const label = value.nombre || value.label || value.name || String(value);
        return { value: value.id || value, label };
      }
      return { value, label: String(value) };
    });
  };

  // Filtrar y ordenar datos
  const filteredAndSortedData = useMemo(() => {
    console.log('=== FILTRADO EN DATATABLE ===');
    console.log('Datos recibidos:', data.length);
    console.log('Término de búsqueda:', searchTerm);
    console.log('Filtros avanzados:', advancedFilters);

    const filtered = data.filter((item) => {
      // Con búsqueda server-side, el texto de 3+ letras ya vino filtrado del API.
      // 1-2 letras (o sin callback) se aplican sobre la página cargada.
      const serverHandledSearch =
        !!onFilterChange && (searchTerm.trim().length >= 3 || !!serverPagination);
      const matchesSearch =
        serverHandledSearch || searchTerm === '' || searchInNestedProperties(item, searchTerm);

      // Filtros avanzados - SOLO aplicar si NO hay callback externo
      const matchesAdvancedFilters = onFilterChange
        ? true
        : filters.every((filter) => {
            // Manejar rangos de fechas primero (tienen lógica especial)
            if (filter.type === 'daterange') {
              const fechaDesde = advancedFilters[`${filter.key}_desde`];
              const fechaHasta = advancedFilters[`${filter.key}_hasta`];

              // Si no hay fechas seleccionadas, no filtrar
              if (!fechaDesde && !fechaHasta) return true;

              // Obtener el valor del item (soporta campos anidados)
              const itemValue = filter.nestedKey
                ? getNestedValue(item, filter.nestedKey)
                : item[filter.key];

              // Si no hay fecha en el item, excluirlo
              if (!itemValue) return false;

              // Convertir a Date y normalizar (solo fecha, sin hora)
              const itemDate = new Date(itemValue);
              itemDate.setHours(0, 0, 0, 0); // Normalizar a inicio del día

              // Verificar que la fecha sea válida
              if (isNaN(itemDate.getTime())) {
                console.warn('Fecha inválida en item:', itemValue, 'para filtro:', filter.key);
                return false;
              }

              // Comparar con fecha desde
              if (fechaDesde) {
                const desdeDate = new Date(fechaDesde);
                desdeDate.setHours(0, 0, 0, 0); // Inicio del día
                if (itemDate < desdeDate) {
                  return false;
                }
              }

              // Comparar con fecha hasta
              if (fechaHasta) {
                const hastaDate = new Date(fechaHasta);
                hastaDate.setHours(0, 0, 0, 0); // Normalizar a inicio del día
                if (itemDate > hastaDate) {
                  return false;
                }
              }

              return true;
            }

            // Para otros tipos de filtros, verificar si hay valor.
            // Importante: false/0 son valores válidos (ej. Activo = No).
            const filterValue = advancedFilters[filter.key];
            if (
              filterValue === '' ||
              filterValue === null ||
              filterValue === undefined ||
              (Array.isArray(filterValue) && filterValue.length === 0)
            ) {
              return true;
            }

            // Obtener el valor del item (soporta campos anidados)
            const itemValue = filter.nestedKey
              ? getNestedValue(item, filter.nestedKey)
              : item[filter.key];

            // itemValue es array (ej. usuarioIds): match si contiene el valor seleccionado
            if (Array.isArray(itemValue)) {
              const ids = itemValue.map((v: any) =>
                v != null && typeof v === 'object' ? v.id : v,
              );
              if (Array.isArray(filterValue)) {
                return filterValue.some((fv) => ids.some((id) => String(id) === String(fv)));
              }
              return ids.some((id) => String(id) === String(filterValue));
            }

            // Manejar arrays (multiselect)
            if (Array.isArray(filterValue)) {
              // Para campos anidados, comparar con el ID o valor completo
              if (filter.nestedKey && typeof itemValue === 'object') {
                return filterValue.some((fv) => fv === itemValue?.id || fv === itemValue);
              }
              return filterValue.includes(itemValue);
            }

            // Manejar booleanos (misma regla que badges: null/undefined ≈ activo)
            if (typeof filterValue === 'boolean') {
              const normalized =
                itemValue !== false && itemValue !== 0 && itemValue !== 'false';
              return normalized === filterValue;
            }

            // Manejar campos anidados para select
            if (filter.nestedKey && typeof itemValue === 'object') {
              const compareValue =
                itemValue?.id || itemValue?.nombre || itemValue?.label || itemValue;
              return (
                String(compareValue) === String(filterValue) ||
                String(compareValue).toLowerCase().includes(String(filterValue).toLowerCase())
              );
            }

            // Comparación de texto estándar
            return String(itemValue).toLowerCase().includes(String(filterValue).toLowerCase());
          });

      const result = matchesSearch && matchesAdvancedFilters;
      if (!result) {
        console.log(
          'Item filtrado:',
          item.id,
          'matchesSearch:',
          matchesSearch,
          'matchesAdvancedFilters:',
          matchesAdvancedFilters,
        );
      }
      return result;
    });

    console.log('Datos después del filtrado:', filtered.length);

    if (sortColumn) {
      filtered.sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [
    data,
    searchTerm,
    sortColumn,
    sortDirection,
    advancedFilters,
    onFilterChange,
    filters,
    serverPagination,
  ]);

  const isServerPaged = !!serverPagination;
  const displayPage = isServerPaged ? serverPagination.page : currentPage;
  const totalPages = isServerPaged
    ? serverPagination.hasMore
      ? displayPage + 1
      : displayPage
    : Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const startIndex = isServerPaged
    ? (displayPage - 1) * serverPagination.pageSize
    : (displayPage - 1) * itemsPerPage;
  const paginatedData = isServerPaged
    ? filteredAndSortedData
    : filteredAndSortedData.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const handleFilterChange = (filterKey: string, value: any) => {
    const newFilters = {
      ...advancedFilters,
      [filterKey]: value,
    };

    filters.forEach((filter) => {
      if (filter.dependsOn === filterKey) {
        newFilters[filter.key] = '';
      }
    });

    setAdvancedFilters(newFilters);
    setCurrentPage(1);

    if (onFilterChange) {
      setTimeout(() => {
        emitServerFilters(newFilters, searchTerm);
      }, 100);
    }
  };

  const handleSearchInputChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
    if (!onFilterChange) return;

    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current);
    }

    const term = value.trim();
    if (term.length > 0 && term.length < 3) return;

    searchTimerRef.current = window.setTimeout(() => {
      emitServerFilters(advancedFilters, value);
    }, 300);
  };

  React.useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        window.clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  const clearFilters = () => {
    if (searchTimerRef.current) {
      window.clearTimeout(searchTimerRef.current);
    }
    setAdvancedFilters({});
    setSearchTerm('');
    setCurrentPage(1);

    if (onFilterChange) {
      onFilterChange({});
    }
  };

  const isFilterDisabled = (filter: FilterConfig) =>
    !!filter.dependsOn &&
    (advancedFilters[filter.dependsOn] === '' || advancedFilters[filter.dependsOn] == null);

  const disabledFilterPlaceholder = (filter: FilterConfig) => {
    const parent = filters.find((f) => f.key === filter.dependsOn);
    return parent ? `Elegí ${parent.label.toLowerCase()} primero` : 'Todos';
  };

  const optionsForFilter = (filter: FilterConfig) => {
    if (isFilterDisabled(filter)) return [];
    // Con filtros server-side no armar opciones desde la página cargada (p. ej. últimos 100).
    if (onFilterChange) return filter.options || [];
    return filter.options || generateFilterOptions(filter);
  };

  const hasActiveFilters = filters.some((filter) => {
    if (filter.type === 'daterange') {
      const fechaDesde = advancedFilters[`${filter.key}_desde`];
      const fechaHasta = advancedFilters[`${filter.key}_hasta`];
      return (fechaDesde && fechaDesde !== '') || (fechaHasta && fechaHasta !== '');
    }
    const value = advancedFilters[filter.key];
    return (
      value !== '' &&
      value !== null &&
      value !== undefined &&
      (!Array.isArray(value) || value.length > 0)
    );
  });

  const unfilteredCount = totalCount ?? data.length;
  const filteredCount = filteredAndSortedData.length;
  const filtersOrSearchApplied = hasActiveFilters || searchTerm.trim().length > 0;
  const recordCountLabel = filtersOrSearchApplied
    ? `${filteredCount} coinciden · ${unfilteredCount} en total`
    : `${unfilteredCount} ${unfilteredCount === 1 ? 'registro' : 'registros'}`;

  const handlePageChange = (page: number) => {
    if (serverPagination) {
      serverPagination.onPageChange(page);
      return;
    }
    setCurrentPage(page);
  };

  // Renderizar vista de cuadrícula
  const renderGridView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {paginatedData.map((row, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="bg-white dark:bg-gray-800 rounded-md shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow duration-200"
        >
          <div className="space-y-3">
            {/* Contenido de la tarjeta */}
            {columns.map((column) => (
              <div key={column.key} className="space-y-1">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {column.label}
                </div>
                <div className="text-xs text-gray-900 dark:text-gray-100">
                  {column.render ? column.render(row[column.key], row) : row[column.key] || 'N/A'}
                </div>
              </div>
            ))}

            {/* Acciones */}
            {((actions && actions.length > 0) || customActions) && (
              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-end space-x-1">
                  {actions?.map((action, actionIndex) => (
                    <button
                      key={actionIndex}
                      onClick={() => action.onClick(row)}
                      className={`p-1.5 rounded-md transition-colors duration-200 ${action.className || 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                      title={action.label}
                    >
                      <action.icon className="w-3.5 h-3.5" />
                    </button>
                  ))}
                  {customActions?.(row)}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );

  // Renderizar vista de tabla
  const renderTableView = () => (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
                    column.sortable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700' : ''
                  }`}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {column.sortable &&
                      sortColumn === column.key &&
                      (sortDirection === 'asc' ? (
                        <ChevronUpIcon className="w-3 h-3" />
                      ) : (
                        <ChevronDownIcon className="w-3 h-3" />
                      ))}
                  </div>
                </th>
              ))}
              {((actions && actions.length > 0) || customActions) && (
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Acciones
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white dark:!bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {paginatedData.map((row, index) => (
              <motion.tr
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white dark:!bg-gray-800 hover:bg-gray-50 dark:hover:!bg-gray-700 transition-colors duration-150"
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className="px-4 py-2 whitespace-nowrap text-xs text-gray-900 dark:text-gray-100"
                  >
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </td>
                ))}
                {((actions && actions.length > 0) || customActions) && (
                  <td className="px-4 py-2 whitespace-nowrap text-right text-xs font-medium">
                    <div className="flex items-center justify-end space-x-1">
                      {actions
                        ?.filter((action) => !action.hidden)
                        .map((action, actionIndex) => (
                          <button
                            key={actionIndex}
                            onClick={() => action.onClick(row)}
                            className={`p-1.5 rounded-md transition-colors duration-200 ${action.className || 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                            title={action.label}
                          >
                            <action.icon className="w-3.5 h-3.5" />
                          </button>
                        ))}
                      {customActions?.(row)}
                    </div>
                  </td>
                )}
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (loading && data.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Solo mostrar estado vacío "plano" si no hay panel de filtros (no ocultar filtros del gate).
  if (
    data.length === 0 &&
    !hasActiveFilters &&
    searchTerm === '' &&
    filters.length === 0 &&
    !onFilterChange
  ) {
    return (
      <div className="space-y-6">
        {/* Header con botón Agregar */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          </div>
          <div className="flex items-center space-x-4">
            {onRefresh && (
              <button
                onClick={onRefresh}
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Intentar de nuevo
              </button>
            )}
            {onAdd && (
              <button onClick={onAdd} className="btn-primary flex items-center space-x-2">
                <PlusIcon className="w-4 h-4" />
                <span>Agregar</span>
              </button>
            )}
          </div>
        </div>

        {/* Estado vacío */}
        <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-gray-500 dark:text-gray-400 text-base mb-3">{emptyMessage}</div>
          <p className="text-gray-400 dark:text-gray-500 text-xs">
            {onAdd
              ? 'Haz clic en "Agregar" para crear el primer registro'
              : 'No hay datos disponibles para mostrar'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative space-y-4">
      {loading && data.length > 0 && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 dark:bg-gray-900/40 pointer-events-none rounded-lg">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          {title && (
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          )}
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {isServerPaged
              ? `${recordCountLabel} · página ${displayPage}${serverPagination.hasMore ? ' · hay más' : ''}`
              : recordCountLabel}
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {onRefresh && (
            <button
              onClick={onRefresh}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
              title="Actualizar"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          )}
          {onAdd && (
            <button onClick={onAdd} className="btn-primary flex items-center space-x-2">
              <PlusIcon className="w-4 h-4" />
              <span>Agregar</span>
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-700 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => handleSearchInputChange(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                autoComplete="off"
              />
            </div>
            {onFilterChange && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {searchHint
                  ? searchHint
                  : searchTerm.trim().length > 0 && searchTerm.trim().length < 3
                    ? 'Escribí al menos 3 letras para buscar en todos los registros. Con menos, se filtra solo esta lista.'
                    : 'Lista inicial: últimos registros. Usá filtros o al menos 3 letras para buscar en todo.'}
              </p>
            )}
          </div>

          {enableAdvancedFilters && filters.length > 0 && (
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-1.5 px-3 py-1.5 text-sm border rounded-md transition-colors duration-200 relative self-start ${
                showFilters
                  ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300'
                  : hasActiveFilters
                    ? 'border-primary-400 bg-primary-50 text-primary-600 dark:bg-primary-900/10 dark:text-primary-400'
                    : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500 dark:text-gray-300 dark:hover:text-gray-200'
              }`}
            >
              <FunnelIcon className="w-3.5 h-3.5" />
              <span>Filtros</span>
              {hasActiveFilters && (
                <span className="absolute -top-1 -right-1 bg-primary-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {
                    Object.entries(advancedFilters).filter(
                      ([_, value]) => value !== '' && value !== null && value !== undefined,
                    ).length
                  }
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters &&
        enableAdvancedFilters &&
        filters.length > 0 &&
        (onFilterChange ? (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-3 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-medium text-gray-900 dark:text-white">
                Filtros Avanzados
              </h3>
              <div className="flex items-center space-x-2">
                {hasActiveFilters && (
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {
                      Object.entries(advancedFilters).filter(
                        ([_, value]) => value !== '' && value !== null && value !== undefined,
                      ).length
                    }{' '}
                    filtro(s) activo(s)
                  </div>
                )}
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filters.map((filter) => {
                const filterValue = advancedFilters[filter.key] || '';
                const options = optionsForFilter(filter);

                return (
                  <div key={filter.key}>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {filter.label}
                    </label>

                    {filter.type === 'text' && (
                      <input
                        type="text"
                        value={filterValue}
                        onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                        placeholder={
                          filter.placeholder || `Filtrar por ${filter.label.toLowerCase()}`
                        }
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      />
                    )}

                    {filter.type === 'select' && (
                      <select
                        value={filterValue}
                        onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                        disabled={isFilterDisabled(filter)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <option value="">
                          {isFilterDisabled(filter) ? disabledFilterPlaceholder(filter) : 'Todos'}
                        </option>
                        {optionsForFilter(filter).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {filter.type === 'multiselect' && (
                      <select
                        multiple
                        value={Array.isArray(filterValue) ? filterValue : []}
                        onChange={(e) => {
                          const selectedValues = Array.from(
                            e.target.selectedOptions,
                            (option) => option.value,
                          );
                          handleFilterChange(filter.key, selectedValues);
                        }}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      >
                        {options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {filter.type === 'boolean' && (
                      <select
                        value={
                          advancedFilters[filter.key] === true
                            ? 'true'
                            : advancedFilters[filter.key] === false
                              ? 'false'
                              : ''
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          handleFilterChange(filter.key, v === '' ? '' : v === 'true');
                        }}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">Todos</option>
                        <option value="true">Sí</option>
                        <option value="false">No</option>
                      </select>
                    )}

                    {filter.type === 'date' && (
                      <input
                        type="date"
                        value={filterValue}
                        onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      />
                    )}

                    {filter.type === 'daterange' && (
                      <div className="space-y-2">
                        <input
                          type="date"
                          value={advancedFilters[`${filter.key}_desde`] || ''}
                          onChange={(e) =>
                            handleFilterChange(`${filter.key}_desde`, e.target.value)
                          }
                          placeholder="Desde"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                        />
                        <input
                          type="date"
                          value={advancedFilters[`${filter.key}_hasta`] || ''}
                          onChange={(e) =>
                            handleFilterChange(`${filter.key}_hasta`, e.target.value)
                          }
                          placeholder="Hasta"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-md p-3 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-medium text-gray-900 dark:text-white">
                Filtros Avanzados
              </h3>
              <div className="flex items-center space-x-2">
                {hasActiveFilters && (
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    {
                      Object.entries(advancedFilters).filter(
                        ([_, value]) => value !== '' && value !== null && value !== undefined,
                      ).length
                    }{' '}
                    filtro(s) activo(s)
                  </div>
                )}
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {filters.map((filter) => {
                const filterValue = advancedFilters[filter.key] || '';
                const options = optionsForFilter(filter);

                return (
                  <div key={filter.key}>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {filter.label}
                    </label>

                    {filter.type === 'text' && (
                      <input
                        type="text"
                        value={filterValue}
                        onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                        placeholder={
                          filter.placeholder || `Filtrar por ${filter.label.toLowerCase()}`
                        }
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      />
                    )}

                    {filter.type === 'select' && (
                      <select
                        value={filterValue}
                        onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                        disabled={isFilterDisabled(filter)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <option value="">
                          {isFilterDisabled(filter) ? disabledFilterPlaceholder(filter) : 'Todos'}
                        </option>
                        {optionsForFilter(filter).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {filter.type === 'multiselect' && (
                      <select
                        multiple
                        value={Array.isArray(filterValue) ? filterValue : []}
                        onChange={(e) => {
                          const selectedValues = Array.from(
                            e.target.selectedOptions,
                            (option) => option.value,
                          );
                          handleFilterChange(filter.key, selectedValues);
                        }}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      >
                        {options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {filter.type === 'boolean' && (
                      <select
                        value={
                          advancedFilters[filter.key] === true
                            ? 'true'
                            : advancedFilters[filter.key] === false
                              ? 'false'
                              : ''
                        }
                        onChange={(e) => {
                          const v = e.target.value;
                          handleFilterChange(filter.key, v === '' ? '' : v === 'true');
                        }}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">Todos</option>
                        <option value="true">Sí</option>
                        <option value="false">No</option>
                      </select>
                    )}

                    {filter.type === 'date' && (
                      <input
                        type="date"
                        value={filterValue}
                        onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                      />
                    )}

                    {filter.type === 'daterange' && (
                      <div className="space-y-2">
                        <input
                          type="date"
                          value={advancedFilters[`${filter.key}_desde`] || ''}
                          onChange={(e) =>
                            handleFilterChange(`${filter.key}_desde`, e.target.value)
                          }
                          placeholder="Desde"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                        />
                        <input
                          type="date"
                          value={advancedFilters[`${filter.key}_hasta`] || ''}
                          onChange={(e) =>
                            handleFilterChange(`${filter.key}_hasta`, e.target.value)
                          }
                          placeholder="Hasta"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

      {/* Content based on view mode */}
      {filteredAndSortedData.length === 0 ? (
        <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="text-gray-500 dark:text-gray-400 text-base mb-3">
            {hasActiveFilters || searchTerm
              ? 'No se encontraron resultados con los filtros aplicados'
              : emptyMessage}
          </div>
          <p className="text-gray-400 dark:text-gray-500 text-xs mb-3">
            {hasActiveFilters || searchTerm
              ? 'Intenta ajustar los filtros o limpiar la búsqueda'
              : searchHint || 'No hay datos disponibles para mostrar'}
          </p>
          {(hasActiveFilters || searchTerm) && (
            <button onClick={clearFilters} className="btn-secondary text-xs px-3 py-1.5">
              Limpiar filtros
            </button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        renderGridView()
      ) : (
        renderTableView()
      )}

      {/* Pagination */}
      {isServerPaged
        ? (displayPage > 1 || serverPagination.hasMore || filteredAndSortedData.length > 0) && (
            <ServerPageBar
              page={displayPage}
              pageSize={serverPagination.pageSize}
              hasMore={serverPagination.hasMore}
              rowCount={filteredCount}
              totalCount={serverPagination.totalCount}
              loading={loading}
              onPageChange={handlePageChange}
            />
          )
        : totalPages > 1 && (
            <div className="bg-transparent px-3 py-2 flex items-center justify-between sm:px-4 pagination-container">
              <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <p className="text-xs text-gray-700 dark:text-gray-300">
                    Mostrando <span className="font-medium">{startIndex + 1}</span> a{' '}
                    <span className="font-medium">
                      {Math.min(startIndex + itemsPerPage, filteredCount)}
                    </span>{' '}
                    de <span className="font-medium">{filteredCount}</span>
                    {filtersOrSearchApplied ? (
                      <>
                        {' '}
                        coincidencias · <span className="font-medium">{unfilteredCount}</span> en
                        total
                      </>
                    ) : (
                      <> resultados</>
                    )}
                  </p>
                </div>
                <div className="bg-transparent">
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-1.5 py-1.5 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="w-4 h-4" />
                    </button>

                    {/* Botón para ir a páginas anteriores */}
                    {currentPage > 10 && (
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 10))}
                        className="relative inline-flex items-center px-2 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        ...
                      </button>
                    )}

                    {/* Páginas visibles (máximo 10) */}
                    {(() => {
                      const maxVisiblePages = 10;
                      const startPage = Math.max(
                        1,
                        Math.min(
                          currentPage - Math.floor(maxVisiblePages / 2),
                          totalPages - maxVisiblePages + 1,
                        ),
                      );
                      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                      return Array.from(
                        { length: endPage - startPage + 1 },
                        (_, i) => startPage + i,
                      ).map((page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`relative inline-flex items-center px-2 py-1.5 border text-xs font-medium ${
                            page === currentPage
                              ? 'z-10 bg-primary-50 dark:bg-primary-900/20 border-primary-500 dark:border-primary-400 text-primary-600 dark:text-primary-400'
                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                        >
                          {page}
                        </button>
                      ));
                    })()}

                    {/* Botón para ir a páginas siguientes */}
                    {currentPage < totalPages - 10 && (
                      <button
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 10))}
                        className="relative inline-flex items-center px-2 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        ...
                      </button>
                    )}

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-1.5 py-1.5 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="w-4 h-4" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
    </div>
  );
};

export default DataTable;
